import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement
} from '@stripe/react-stripe-js'
import { useStripeContext, useDebounce } from '../../../../contexts/StripeContext'

interface StripePaymentFormProps {
  setupIntent: any
  /** Called with the confirmed SetupIntent's id, when Stripe returns it. */
  onSuccess: (setupIntentId?: string) => void
  onCancel?: () => void
  onError: (error: string) => void
  /** The onboarding card step has no way out but saving a card (SC-438). */
  hideCancel?: boolean
  submitLabel?: string
  /**
   * Hides Link, and with it the Bank tab Stripe offers through Link, which a card-only
   * SetupIntent does not keep out of the Payment Element (SC-438 onboarding asks for a card).
   */
  hideLink?: boolean
}

const CardPaymentForm: React.FC<StripePaymentFormProps> = ({
  setupIntent,
  onSuccess,
  onCancel,
  onError,
  hideCancel = false,
  submitLabel = 'Add Payment Method',
  hideLink = false,
}) => {
  const theme = useTheme()
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounce error state to prevent rapid updates
  const debouncedError = useDebounce(error, 300)

  // Debug: Log setup intent details (only in development)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && setupIntent) {
      console.log('CardPaymentForm received setupIntent:', {
        id: setupIntent.id,
        status: setupIntent.status,
        client_secret: setupIntent.client_secret?.substring(0, 20) + '...',
        customer: setupIntent.customer,
        usage: setupIntent.usage
      })
    }
  }, [setupIntent])

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      console.error('Stripe or Elements not loaded')
      return
    }

    setLoading(true)
    setError(null)

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Submitting payment method setup...')
      }
      
      const { error, setupIntent: confirmedSetupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/billing/success',
        },
        redirect: 'if_required'
      })

      if (process.env.NODE_ENV === 'development') {
        console.log('Setup result:', { error, setupIntent: confirmedSetupIntent })
      }

      if (error) {
        console.error('Setup error:', error)
        const errorMessage = error.message || 'Failed to add payment method'
        setError(errorMessage)
        onError(errorMessage)
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Payment method added successfully')
        }
        onSuccess(confirmedSetupIntent?.id)
      }
    } catch (err: any) {
      console.error('Unexpected error:', err)
      const errorMessage = err.message || 'An unexpected error occurred'
      setError(errorMessage)
      onError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [stripe, elements, onSuccess, onError])

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
                    borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'action.hover',
                    backgroundImage: 'none',
          }}
        >
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
            Credit Card Information
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <PaymentElement 
              options={{
                layout: 'tabs',
                ...(hideLink ? { wallets: { link: 'never' as const } } : {}),
              }}
            />
          </Box>

          {debouncedError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {debouncedError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            {!hideCancel && (
            <Button
              type="button"
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              size="large"
              sx={{
                color: 'text.secondary',
                borderColor: 'divider',
                borderWidth: '1.5px',
                borderRadius: '8px',
                px: 3,
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  backgroundImage: 'none',
                  color: 'primary.main',
                  borderWidth: '1.5px',
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[4]
                },
                '&:disabled': {
                  color: 'text.disabled',
                  borderColor: 'divider',
                  backgroundColor: 'transparent'
                }
              }}
            >
              Cancel
            </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!stripe || loading}
              size="large"
              sx={{
                borderRadius: '8px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                boxShadow: theme.shadows[3],
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  backgroundImage: 'none',
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[6]
                },
                '&:disabled': {
                  bgcolor: alpha(theme.palette.primary.main, 0.45),
                  backgroundImage: 'none',
                  color: 'primary.contrastText',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Adding...
                  </Typography>
                </Box>
              ) : (
                submitLabel
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </form>
  )
}

export const StripePaymentForm: React.FC<StripePaymentFormProps> = (props) => {
  const theme = useTheme()
  const { stripe, loading, error, isInitialized } = useStripeContext()

  // Memoize the Elements options to prevent unnecessary re-renders
  const elementsOptions = useMemo(() => ({
    clientSecret: props.setupIntent?.client_secret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: theme.palette.primary.main,
        colorBackground: theme.palette.background.paper,
        colorText: theme.palette.text.primary,
        colorDanger: theme.palette.error.main,
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      }
    }
  }), [props.setupIntent?.client_secret, theme])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    )
  }

  if (!isInitialized || !stripe) {
    return (
      <Alert severity="error">
        Stripe not initialized
      </Alert>
    )
  }

  if (!props.setupIntent?.client_secret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    )
  }

  return (
    <Elements 
      stripe={stripe}
      options={elementsOptions}
    >
      <CardPaymentForm {...props} />
    </Elements>
  )
}